import { Component, OnInit } from "@angular/core";
import { environment } from "../../../environments/environment";
import { Lock } from "../../models/lock";
import { NgbModal, NgbModalRef } from "@ng-bootstrap/ng-bootstrap";
import { LockService, BikeLink } from "../../services/lock.service";
import { LocksFormComponent } from "../locks-form/locks-form.component";
import { LocksDeleteComponent } from "../locks-delete/locks-delete.component";
import { StationLink } from "../../services/attachment-point.service";
import { fadeInAndOut } from "../../utils/animations-utils";
import { AccountService } from "../../services/account.service";
import { TranslateService } from "@ngx-translate/core";
import { ToastsManager } from "ng2-toastr";

@Component({
  selector: "app-locks",
  animations: [fadeInAndOut()],
  templateUrl: "./locks-list.component.html",
  styleUrls: ["./locks-list.component.css"],
})
export class LocksListComponent implements OnInit {
  p: number = 1; // current page
  total: number; // total count of records
  itemsPerPage = environment.itemPerPage; // number of records to display on each page
  listLoading: boolean;
  downloading: boolean = false;
  filters: Map<string, any>;
  displayNulls: boolean = true;

  ID_FILTER = "l.id";
  NAME_FILTER = "l.name";
  TYPE_FILTER = "l.type";
  BIKE_FILTER = "bikeName";
  GROUP_FILTER = "l.group.name";
  DELETED_FILTER = "l.deleted";

  /**
   * Keys : string criterias
   * Values : boolean => true if ascending, false otherwise
   */
  sortingCriterias: Map<string, boolean> = new Map();
  typeFilter = "ALL";
  listFiltered = false;

  /** lock to display on the table */
  locks: Lock[] = [];

  /** Modal windows */
  lockToUpdate: Lock;
  lockToDelete: Lock;
  deleteModal: NgbModalRef;
  formModal: NgbModalRef;
  multipleDeleteModal: NgbModalRef;

  /** Selected locks */
  selectedLocks: Map<Lock, boolean> = new Map();
  selectAllChecked: boolean = false;

  constructor(
    private modalService: NgbModal,
    private lockService: LockService,
    public accountService: AccountService,
    public toast: ToastsManager,
    public translate: TranslateService
  ) {}

  /**
   * Loads first page of results ordered by ID
   */
  ngOnInit() {
    this.initFilters();
    if (localStorage.getItem("itemsPerPage")) {
      this.itemsPerPage = parseInt(localStorage.getItem("itemsPerPage"));
    }
    this.getPage(1);
  }

  /**
   * Create a new empty object for filtering attachment points
   * Retrieve first page
   */
  clearFilters() {
    this.listFiltered = false;
    this.initFilters();
    this.reloadList();
  }

  /**
   * Sorting system function as follow
   * On first click on a criteria => add the criteria as ascending
   * On second click => put the critiera to descending
   * On third click => remove the criteria from the map
   */
  updateSortingCriterias(criteria: string) {
    if (this.sortingCriterias.has(criteria)) {
      if (this.sortingCriterias.get(criteria)) {
        // criteria is present as true => switch to false
        this.sortingCriterias.set(
          criteria,
          !this.sortingCriterias.get(criteria)
        );
      } else {
        // criteria present as false => delete it
        this.sortingCriterias.delete(criteria);
      }
    } else {
      this.sortingCriterias.set(criteria, true);
    }
  }

  initFilters() {
    this.filters = new Map();
  }

  changeItemCount(value) {
    this.itemsPerPage = value;
    this.reloadList();

    localStorage.setItem("itemsPerPage", value);
  }

  sortBy(criteria: string) {
    this.updateSortingCriterias(criteria);
    this.reloadList();
  }

  onTypeChange() {
    this.updateFilters();
    this.reloadList();
  }

  updateFilters() {
    if (this.typeFilter == "ALL") {
      this.filters[this.TYPE_FILTER] = undefined;
    } else {
      this.filters[this.TYPE_FILTER] = this.typeFilter;
    }
  }

  /**
   * Reload lists according to filters, sorting criterias from the first page
   */
  reloadList() {
    this.listFiltered = false;
    Object.values(this.filters).forEach((value) => {
      if (value) {
        this.listFiltered = true;
      }
    });
    this.getPage(1);
  }

  getPage(page: number) {
    this.p = page;
    this.listLoading = true;
    this.lockService
      .getPaginatedFilteredSortedLocks(
        this.p,
        this.itemsPerPage,
        this.filters,
        this.sortingCriterias,
        this.displayNulls ? BikeLink.ALL : BikeLink.ONLY_LINKED
      )
      .subscribe((data) => {
        this.locks = data.res;
        this.total = data.headers.get("x-total-count");
        this.listLoading = false;
      });
  }

  /* ------------- Modals ------------------- */
  onNewClick(): void {
    this.formModal = this.modalService.open(LocksFormComponent, {
      size: "lg",
      backdrop: "static",
    });
    this.formModal.componentInstance.creationMode = true;
    this.formModal.componentInstance.lock = new Lock();
    this.formModal.componentInstance.onCreateValidation.subscribe((result) => {
      this.getPage(this.p);
    });
  }

  onUpdateClick(l) {
    this.formModal = this.modalService.open(LocksFormComponent, {
      size: "lg",
      backdrop: "static",
    });
    this.formModal.componentInstance.creationMode = false;
    this.formModal.componentInstance.lock = JSON.parse(JSON.stringify(l));
    this.formModal.componentInstance.onUpdateValidation.subscribe((result) => {
      this.getPage(this.p);
    });
  }

  onDeleteClick(l) {
    this.lockToDelete = l;
    this.deleteModal = this.modalService.open(LocksDeleteComponent, {
      centered: true,
    });
    this.deleteModal.componentInstance.lock = this.lockToDelete;
    this.deleteModal.componentInstance.onDeleteValidation.subscribe((d) =>
      this.getPage(this.p)
    );
  }

  selectGrp(lock: Lock) {
    this.selectedLocks.set(lock, true);
  }

  deselectGrp(lock: Lock) {
    this.selectedLocks.delete(lock);
    this.selectAllChecked = false;
  }

  onCheckboxSelectAllGrpChange() {
    if (this.selectedLocks.size < this.locks.length) {
      this.locks.forEach((ap) => this.selectedLocks.set(ap, true));
    } else {
      this.selectedLocks.clear();
    }
  }

  onCheckboxChange(g: Lock) {
    if (!this.selectedLocks.get(g)) {
      this.selectGrp(g);
    } else {
      this.deselectGrp(g);
    }
  }

  deleteSelection() {
    if (this.selectedLocks.size > 0) {
      //this.multipleDeleteModal = this.modalService.open(AttachmentpointsMultipleDeleteComponent, {size: "lg", backdrop: "static"});
      // this.multipleDeleteModal.componentInstance.attachmentPoints = Array.from(this.selectedAPs.keys());
    }
  }

  downloadXLSFile() {
    this.downloading = true;
    this.lockService
      .downloadXLSFile(
        this.p,
        this.itemsPerPage,
        this.filters,
        this.sortingCriterias,
        this.displayNulls ? BikeLink.ALL : BikeLink.ONLY_LINKED
      )
      .subscribe((isTruncated) => {
        if (isTruncated) {
          this.toast.warning(this.translate.instant("common.downloadOk"));
        } else {
          this.toast.success(
            this.translate.instant("common.downloadTruncated")
          );
        }
        this.downloading = false;
      });
  }
}



// WEBPACK FOOTER //
// ./src/app/locks/locks-list/locks-list.component.ts